import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useConfig } from '@entities/config/api';
import { ROUTES } from '@shared/config/routes';
import { Skeleton } from '@shared/ui';

import { LegalArticle } from './LegalArticle';
import styles from './LegalArticle.module.css';

/**
 * Datenschutzerklärung — Art. 12–14 DSGVO.
 *
 * Текст описывает **фактическую** конфигурацию системы, а не типовой шаблон:
 * OTP-вход и серверные сессии, гостевой контур с отдельной cookie, вызовы LLM
 * через провайдера в США, исходящий SMTP и входящий IMAP для переписки с
 * автохаусами, Telegram как канал пользователя, импорт объявлений, загруженные
 * пользователем документы, хеши IP в журналах.
 *
 * Два места, где шаблоны обычно врут и где врать нельзя:
 *
 * * **обязательная регистрация.** Шаги 1–2 проходятся без аккаунта
 *   (MVP_PRD §41), поэтому утверждать, что аккаунт обязателен, нельзя;
 * * **автоматизированные решения.** Match Score — не решение по Art. 22 DSGVO:
 *   он ничего не порождает и не заменяет выбор человека. Но раскрыть его
 *   логику всё равно нужно, а не умолчать.
 */
export function PrivacyPage() {
  const { t } = useTranslation();
  const { data, isPending } = useConfig();

  if (isPending || !data) {
    return (
      <LegalArticle title="Datenschutzerklärung">
        <Skeleton height="24rem" radius="var(--radius-card)" />
      </LegalArticle>
    );
  }

  const operator = data.legal.operator;
  const guestTtl = data.guest.session_ttl_days;

  return (
    <LegalArticle
      title="Datenschutzerklärung"
      version={data.legal.document_versions.privacy}
      lead={<p>{t('legal.privacy.lead')}</p>}
    >
      <h2>1. Verantwortlicher</h2>
      <p>
        Verantwortlich im Sinne der DSGVO ist die {operator.legal_name}, {operator.street},{' '}
        {operator.postal_code} {operator.city}, {operator.country}, vertreten durch{' '}
        {operator.managing_director}. E-Mail:{' '}
        <a href={`mailto:${operator.email}`}>{operator.email}</a>
        {operator.phone ? `, Telefon: ${operator.phone}` : ''}.
      </p>
      <p>
        Zuständige Aufsichtsbehörde ist die staatliche Datenschutzinspektion der Republik Litauen
        (Valstybinė duomenų apsaugos inspekcija). Sie können sich auch an die Aufsichtsbehörde Ihres
        gewöhnlichen Aufenthaltsorts wenden.
      </p>

      <h2>2. Welche Daten wir verarbeiten und warum</h2>
      <div className={styles.tableWrap}>
        <table>
          <thead>
            <tr>
              <th>Zweck</th>
              <th>Daten</th>
              <th>Rechtsgrundlage</th>
              <th>Speicherdauer</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Aufruf der Website, Betriebssicherheit</td>
              <td>
                Zeitpunkt, angeforderte Adresse, Statuscode, gekürzter bzw. mit einem Schlüssel
                gehashter IP-Wert, Browserkennung als Hashwert
              </td>
              <td>Art. 6 Abs. 1 lit. f — Betrieb und Missbrauchsabwehr</td>
              <td>bis zu 30 Tage</td>
            </tr>
            <tr>
              <td>Nutzung ohne Konto (Gastzugang)</td>
              <td>Gast-Sitzungskennung im Cookie, Suchkriterien, Dialogverlauf</td>
              <td>Art. 6 Abs. 1 lit. b — vorvertragliche Nutzung auf Ihre Anfrage</td>
              <td>{guestTtl} Tage ab der letzten Aktivität</td>
            </tr>
            <tr>
              <td>Anmeldung per Einmalcode</td>
              <td>E-Mail-Adresse, Prüfwert des Codes, Sitzungskennung, Zeitstempel</td>
              <td>Art. 6 Abs. 1 lit. b — Durchführung des Nutzungsverhältnisses</td>
              <td>Sitzung 30 Tage; Codes 10 Minuten</td>
            </tr>
            <tr>
              <td>Dialog mit dem KI-Assistenten Karl</td>
              <td>Ihre Nachrichten und die daraus abgeleiteten Suchkriterien</td>
              <td>Art. 6 Abs. 1 lit. b — Erbringung der Dienstleistung</td>
              <td>bis zur Löschung des Auftrags oder des Kontos</td>
            </tr>
            <tr>
              <td>Bestellung und Vertragsbestätigung</td>
              <td>Auftragsnummer, Betrag, Belegnummer, Zeitpunkt und Fassung Ihrer Einwilligung</td>
              <td>Art. 6 Abs. 1 lit. b und lit. c — Vertrag und Nachweispflichten</td>
              <td>bis zu 10 Jahre (handels- und steuerrechtliche Aufbewahrung)</td>
            </tr>
            <tr>
              <td>Anfragen und Verhandlung mit Autohäusern</td>
              <td>
                Ihr Anliegen in der von uns erstellten, sachlichen Form; Ihre Identität wird nur
                insoweit übermittelt, wie es für die konkrete Anfrage nötig ist
              </td>
              <td>Art. 6 Abs. 1 lit. b — Erbringung der beauftragten Leistung</td>
              <td>bis zur Löschung des Auftrags</td>
            </tr>
            <tr>
              <td>Von Ihnen hochgeladene Dokumente</td>
              <td>Datei und die daraus wörtlich übernommenen Felder</td>
              <td>Art. 6 Abs. 1 lit. b — von Ihnen beauftragter Abgleich</td>
              <td>180 Tage oder bis zur Löschung durch Sie</td>
            </tr>
            <tr>
              <td>Widerrufserklärungen</td>
              <td>Name, Vertragsangabe, Kontaktadresse, Eingangszeitpunkt</td>
              <td>Art. 6 Abs. 1 lit. c — § 356a Abs. 4 BGB</td>
              <td>bis zu 3 Jahre (Verjährung)</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>3. Empfänger und Auftragsverarbeiter</h2>
      <ul>
        <li>
          <strong>Hosting und Datenbank:</strong> Auftragsverarbeiter innerhalb der Europäischen
          Union, gebunden durch einen Vertrag nach Art. 28 DSGVO.
        </li>
        <li>
          <strong>Sprachmodell (KI-Assistent Karl):</strong> Ihre Nachrichten werden zur
          Verarbeitung an einen Anbieter von Sprachmodellen übermittelt. Dabei findet eine
          Übermittlung in die Vereinigten Staaten statt; sie ist durch Standardvertragsklauseln nach
          Art. 46 Abs. 2 lit. c DSGVO abgesichert. Der Anbieter speichert die Inhalte nicht zu
          eigenen Zwecken und trainiert damit keine Modelle.
        </li>
        <li>
          <strong>E-Mail-Versand und -Empfang:</strong> Ihre E-Mail-Adresse wird für den Versand von
          Einmalcodes und Vertragsbestätigungen verarbeitet. Für die Korrespondenz mit Autohäusern
          nutzt CarViQ.DE eigene Adressen.
        </li>
        <li>
          <strong>Autohäuser:</strong> erhalten ausschließlich die für die jeweilige Anfrage
          erforderlichen Angaben. Ihre E-Mail-Adresse wird nicht weitergegeben.
        </li>
        <li>
          <strong>Telegram:</strong> nur, wenn Sie diesen Kanal selbst verbinden. Dann gelten
          zusätzlich die Bedingungen des Anbieters.
        </li>
      </ul>
      <p>
        Zahlungsdienstleister sind nicht eingebunden: Zahlungsdaten wie Kartennummern oder IBAN
        werden weder erhoben noch gespeichert. Entsprechende Felder existieren im System nicht.
      </p>

      <h2>4. Fahrzeugdaten aus öffentlichen Angeboten</h2>
      <p>
        Der CarViQ.DE-Katalog wird aus öffentlich zugänglichen Fahrzeugangeboten von Autohäusern
        befüllt. Verarbeitet werden dabei Angaben zum Fahrzeug und zum Autohaus, nicht zu Ihnen.
        Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO — unser Interesse an einem aussagekräftigen
        Katalog.
      </p>

      <h2>5. Cookies und ähnliche Technologien</h2>
      <p>
        CarViQ.DE setzt technisch notwendige Cookies ein, die nach § 25 Abs. 2 TDDDG keine
        Einwilligung erfordern. Welche das sind, wofür sie dienen und wie lange sie gespeichert
        werden, listet die <Link to={ROUTES.cookies}>Cookie-Richtlinie</Link> auf.
        {data.legal.optional_cookie_categories.length === 0
          ? ' Analyse- oder Marketing-Technologien werden derzeit nicht eingesetzt.'
          : ' Nicht notwendige Technologien werden erst nach Ihrer Einwilligung geladen; diese können Sie jederzeit über die Cookie-Einstellungen im Seitenfuß ändern.'}
      </p>

      <h2>6. Automatisierte Auswertung</h2>
      <p>
        CarViQ.DE berechnet für jedes Fahrzeug einen Übereinstimmungswert (Match Score) aus Ihren
        Kriterien und den Katalogdaten. Das Verfahren ist fest programmiert und für jedes Ergebnis
        nachvollziehbar begründet. Eine automatisierte Entscheidung mit rechtlicher Wirkung oder
        ähnlich erheblicher Beeinträchtigung im Sinne des Art. 22 DSGVO findet nicht statt: Sie
        entscheiden, welche Fahrzeuge weiterverfolgt werden und ob Sie kaufen.
      </p>

      <h2>7. Ihre Rechte</h2>
      <p>
        Sie haben das Recht auf Auskunft (Art. 15), Berichtigung (Art. 16), Löschung (Art. 17),
        Einschränkung der Verarbeitung (Art. 18), Datenübertragbarkeit (Art. 20) sowie das Recht,
        einer auf Art. 6 Abs. 1 lit. f gestützten Verarbeitung zu widersprechen (Art. 21). Erteilte
        Einwilligungen können Sie jederzeit mit Wirkung für die Zukunft widerrufen. Außerdem steht
        Ihnen ein Beschwerderecht bei einer Aufsichtsbehörde zu (Art. 77).
      </p>
      <p>
        Auftrag, Dialogverlauf und Konto können Sie im Bereich „Mein Konto" selbst exportieren und
        löschen. Für alle übrigen Anliegen genügt eine E-Mail an{' '}
        <a href={`mailto:${operator.email}`}>{operator.email}</a>.
      </p>

      <h2>8. Pflicht zur Bereitstellung</h2>
      <p>
        Ein Konto ist für die Nutzung der ersten Schritte nicht erforderlich — Suchanfrage und
        Auswertung sind ohne Registrierung möglich. Erst für eine kostenpflichtige Bestellung
        benötigen wir eine E-Mail-Adresse; ohne sie kann kein Vertrag geschlossen und keine
        Vertragsbestätigung zugestellt werden.
      </p>

      <h2>9. Änderungen</h2>
      <p>
        Ändert sich die technische Konfiguration — etwa durch Einbindung weiterer Dienste —
        aktualisieren wir diese Erklärung. Die jeweils geltende Fassung ist an dieser Stelle mit
        Datum abrufbar.
      </p>
    </LegalArticle>
  );
}
